import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  CancelProcedureSchema,
  CreateProcedureResponseSchema,
  CreateProcedureSchema,
  ProcedureListItemSchema,
  ProcedureSchema,
  TransitionProcedureSchema,
  UpdatePatientFlowSchema,
  UpdateProcedureSchema,
  type CancelProcedureInput,
  type CreateProcedureInput,
  type CreateProcedureResponse,
  type EpisodeStatus,
  type PatientFlowStatus,
  type PatientId,
  type Procedure,
  type ProcedureId,
  type ProcedureListItem,
  type ProcedureType,
  type Room,
  type TransitionProcedureInput,
  type UpdatePatientFlowInput,
  type UpdateProcedureInput,
  type UserId,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

/**
 * Hooks de acceso a `/api/procedures`. Cada query/mutation valida la respuesta
 * con su schema Zod compartido — si el backend cambia de forma incompatible
 * el error se manifiesta como `ApiClientError` en el `onError`.
 */

const ProcedureListResponseSchema = z.object({
  items: z.array(ProcedureListItemSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});

export type ProcedureListResponse = z.infer<typeof ProcedureListResponseSchema>;

export interface ProceduresListParams {
  page: number;
  pageSize: number;
  fecha?: string;
  desde?: string;
  hasta?: string;
  sala?: Room;
  tipo?: ProcedureType;
  estado?: EpisodeStatus;
  estadoPaciente?: PatientFlowStatus;
  medicoId?: UserId;
  pacienteId?: PatientId;
}

export const proceduresKeys = {
  all: ['procedures'] as const,
  list: (params: ProceduresListParams) => ['procedures', 'list', params] as const,
  detail: (id: ProcedureId) => ['procedures', 'detail', id] as const,
};

function buildQs(params: ProceduresListParams): string {
  const qs = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.fecha !== undefined && params.fecha !== '') qs.set('fecha', params.fecha);
  if (params.desde !== undefined && params.desde !== '') qs.set('desde', params.desde);
  if (params.hasta !== undefined && params.hasta !== '') qs.set('hasta', params.hasta);
  if (params.sala !== undefined) qs.set('sala', params.sala);
  if (params.tipo !== undefined) qs.set('tipo', params.tipo);
  if (params.estado !== undefined) qs.set('estado', params.estado);
  if (params.estadoPaciente !== undefined) qs.set('estadoPaciente', params.estadoPaciente);
  if (params.medicoId !== undefined) qs.set('medicoId', params.medicoId);
  if (params.pacienteId !== undefined) qs.set('pacienteId', params.pacienteId);
  return qs.toString();
}

export function useProceduresList(
  params: ProceduresListParams,
): UseQueryResult<ProcedureListResponse, Error> {
  return useQuery({
    queryKey: proceduresKeys.list(params),
    queryFn: async () =>
      apiFetch<ProcedureListResponse>(`/api/procedures?${buildQs(params)}`, {
        parseAs: ProcedureListResponseSchema,
      }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function useProcedureById(
  id: ProcedureId | undefined,
): UseQueryResult<Procedure, Error> {
  return useQuery({
    queryKey:
      id !== undefined
        ? proceduresKeys.detail(id)
        : (['procedures', 'detail', '__missing__'] as const),
    queryFn: async () => {
      if (id === undefined) {
        throw new Error('id requerido');
      }
      return apiFetch<Procedure>(`/api/procedures/${encodeURIComponent(id)}`, {
        parseAs: ProcedureSchema,
      });
    },
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

export function useCreateProcedure(): UseMutationResult<
  CreateProcedureResponse,
  Error,
  CreateProcedureInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProcedureInput) => {
      const body = CreateProcedureSchema.parse(input);
      return apiFetch<CreateProcedureResponse>('/api/procedures', {
        method: 'POST',
        body,
        parseAs: CreateProcedureResponseSchema,
      });
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      qc.setQueryData(proceduresKeys.detail(created.id), created);
    },
  });
}

export interface UpdateProcedureArgs {
  id: ProcedureId;
  body: UpdateProcedureInput;
}

export function useUpdateProcedure(): UseMutationResult<Procedure, Error, UpdateProcedureArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: UpdateProcedureArgs) => {
      const validated = UpdateProcedureSchema.parse(body);
      return apiFetch<Procedure>(`/api/procedures/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: validated,
        parseAs: ProcedureSchema,
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(proceduresKeys.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      void qc.invalidateQueries({ queryKey: proceduresKeys.detail(updated.id) });
    },
  });
}

export interface TransitionProcedureArgs {
  id: ProcedureId;
  body: TransitionProcedureInput;
}

export function useTransitionProcedure(): UseMutationResult<
  Procedure,
  Error,
  TransitionProcedureArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: TransitionProcedureArgs) => {
      const validated = TransitionProcedureSchema.parse(body);
      return apiFetch<Procedure>(`/api/procedures/${encodeURIComponent(id)}/transition`, {
        method: 'POST',
        body: validated,
        parseAs: ProcedureSchema,
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(proceduresKeys.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      void qc.invalidateQueries({ queryKey: proceduresKeys.detail(updated.id) });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export interface CancelProcedureArgs {
  id: ProcedureId;
  body: CancelProcedureInput;
}

export function useCancelProcedure(): UseMutationResult<Procedure, Error, CancelProcedureArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: CancelProcedureArgs) => {
      const validated = CancelProcedureSchema.parse(body);
      return apiFetch<Procedure>(`/api/procedures/${encodeURIComponent(id)}/cancel`, {
        method: 'POST',
        body: validated,
        parseAs: ProcedureSchema,
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(proceduresKeys.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      void qc.invalidateQueries({ queryKey: proceduresKeys.detail(updated.id) });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useDeleteProcedure(): UseMutationResult<
  { deleted: true; id: string },
  Error,
  { id: ProcedureId }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: ProcedureId }) => {
      return apiFetch<{ deleted: true; id: string }>(
        `/api/procedures/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: (_res, { id }) => {
      qc.removeQueries({ queryKey: proceduresKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      // El conteo de la enfermera y el dashboard se recalculan desde la tabla.
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['payroll'] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export interface UpdatePatientFlowArgs {
  id: ProcedureId;
  body: UpdatePatientFlowInput;
}

export function useUpdatePatientFlow(): UseMutationResult<
  Procedure,
  Error,
  UpdatePatientFlowArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: UpdatePatientFlowArgs) => {
      const validated = UpdatePatientFlowSchema.parse(body);
      return apiFetch<Procedure>(
        `/api/procedures/${encodeURIComponent(id)}/patient-status`,
        {
          method: 'POST',
          body: validated,
          parseAs: ProcedureSchema,
        },
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(proceduresKeys.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: proceduresKeys.all });
      void qc.invalidateQueries({ queryKey: proceduresKeys.detail(updated.id) });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export type { Procedure, ProcedureListItem };
